import moment from "moment";
import {
    GET_STAFF_MANAGEMENT_ATTENDANCE_BY_DATE,
    GET_STAFF_MANAGEMENT_ATTENDANCE_BY_SUBCONTRACTOR,
} from "graphql/query/staff-management-gql";
import { STAFF_ATTENDANCE_WARNING_CODES } from "./staff-attendance-session-helper";


export const STAFF_ATTENDANCE_PAGE_SIZE = 25;
export { STAFF_ATTENDANCE_WARNING_CODES };

const API_DATE_FORMAT = "YYYY-MM-DD";
const API_DATE_TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
const NINE_HOURS_IN_SECONDS = 9 * 60 * 60;
const STATUS_PRECEDENCE = [
    "failed_checkout",
    "missed_checkout",
    "in_progress",
    "unknown",
    "completed",
];
const SESSION_STATUS_BY_API_STATUS = {
    COMPLETED: "completed",
    FAILED_CHECKOUT: "failed_checkout",
    MISSED_CHECKOUT: "missed_checkout",
    IN_PROGRESS: "in_progress",
};
const WITHIN_RADIUS_REASON = "WITHIN_RADIUS";
const OUTSIDE_RADIUS_REASON = "OUTSIDE_RADIUS";

const isValidDate = (value) => Boolean(value) && (
    moment.isMoment(value) ?
        value.isValid() :
        moment(value, [ moment.ISO_8601, API_DATE_TIME_FORMAT, "YYYY-MM-DD" ], true).isValid()
);

const getSessionStatus = (session = {}) => {
    const rawStatus = String(session.getCheckOutStatus || "")
        .trim()
        .toUpperCase();
    const normalizedStatus = SESSION_STATUS_BY_API_STATUS[ rawStatus ];

    if (normalizedStatus)
    {
        return normalizedStatus;
    }

    if (rawStatus)
    {
        return "unknown";
    }

    return isValidDate(session.check_out_at) ? "completed" : "in_progress";
};

const getStatusByPrecedence = (sessions) => {
    const statuses = sessions.map(getSessionStatus);

    return STATUS_PRECEDENCE.find((status) => statuses.includes(status)) || "completed";
};

const getDateBoundary = (sessions, field, boundary, completedOnly = false) => {
    const candidates = sessions
        .filter((session) => !completedOnly || getSessionStatus(session) === "completed")
        .map((session) => session?.[ field ])
        .filter(isValidDate);

    if (!candidates.length)
    {
        return null;
    }

    return candidates.reduce((selected, candidate) => {
        const selectedTime = moment(selected).valueOf();
        const candidateTime = moment(candidate).valueOf();

        return boundary === "min" ?
            (candidateTime < selectedTime ? candidate : selected) :
            (candidateTime > selectedTime ? candidate : selected);
    });
};

const getSessionUserKey = (session, index) => (
    session?.user_id ?? session?.relUser?.id ?? session?.id ?? `unknown-${ index }`
);

const getCalendarDate = (value) => {
    const datePrefix = typeof value === "string" ? value.match(/^(\d{4}-\d{2}-\d{2})/)?.[ 1 ] : undefined;

    if (datePrefix && moment(datePrefix, "YYYY-MM-DD", true).isValid())
    {
        return datePrefix;
    }

    return isValidDate(value) ? moment(value).format("YYYY-MM-DD") : null;
};

const getSessionWorkDate = (session, groupBy, groupDate) => {
    const source = groupBy === "subcontractor" ? session?.check_in_at : groupDate;

    return getCalendarDate(source);
};

const toFiniteNumber = (value) => {
    if (value === null || value === undefined || value === "")
    {
        return undefined;
    }

    const number = Number(value);

    return Number.isFinite(number) ? number : undefined;
};

const toNonNegativeNumber = (value) => {
    const number = toFiniteNumber(value);

    return number !== undefined && number >= 0 ? number : undefined;
};

const hasValidScanCoordinates = (snapshot) => {
    const latitude = toFiniteNumber(snapshot?.scan_latitude ?? snapshot?.latitude);
    const longitude = toFiniteNumber(snapshot?.scan_longitude ?? snapshot?.longitude);

    return (
        latitude !== undefined && latitude >= -90 && latitude <= 90 &&
        longitude !== undefined && longitude >= -180 && longitude <= 180
    );
};

const getSnapshotReason = (snapshot) => String(snapshot?.reason || "")
    .trim()
    .toUpperCase();

const getSessionSnapshots = (session) => [
    session?.relCheckInLog,
    session?.relCheckOutLog,
].filter(Boolean);

const getCompletedSessionLocationStatus = (session) => {
    const snapshots = getSessionSnapshots(session);
    const reasons = snapshots.map(getSnapshotReason);

    if (reasons.includes(OUTSIDE_RADIUS_REASON))
    {
        return "outside_zone";
    }

    const snapshotsWithLocation = snapshots.filter(hasValidScanCoordinates);

    if (!snapshotsWithLocation.length)
    {
        return "no_location";
    }

    if (
        snapshots.length !== 2 ||
        snapshotsWithLocation.length !== snapshots.length ||
        !reasons.every((reason) => reason === WITHIN_RADIUS_REASON)
    )
    {
        return "unknown";
    }

    return "inside_zone";
};

const getSessionZoneRadius = (session) => {
    const snapshots = getSessionSnapshots(session);
    const radii = snapshots.map((snapshot) => toFiniteNumber(snapshot?.zone_radius));

    if (!snapshots.length || radii.some((radius) => radius === undefined || radius <= 0))
    {
        return undefined;
    }

    const uniqueRadii = [ ...new Set(radii) ];

    return uniqueRadii.length === 1 ? uniqueRadii[ 0 ] : undefined;
};

const adaptFailedCheckoutSummary = (summary) => {
    if (!summary)
    {
        return undefined;
    }

    const attemptsCount = toNonNegativeNumber(summary.attempts_count);
    const lastDistanceFromZone = toNonNegativeNumber(summary.last_distance_from_zone);
    const lastAttempt = summary.last_failed_log ? {
        ...summary.last_failed_log,
        distance_from_zone: lastDistanceFromZone ?? summary.last_failed_log.getDistanceFromZone,
    } : null;

    return {
        attempts_count: Number.isInteger(attemptsCount) ? attemptsCount : undefined,
        first_attempt_at: summary.first_attempt_at,
        last_attempt_at: summary.last_attempt_at,
        last_distance_from_zone: lastDistanceFromZone,
        last_attempt: lastAttempt,
    };
};

const adaptSession = (session) => {
    const status = getSessionStatus(session);

    return {
        ...session,
        status,
        total_worked_seconds: staffAttendanceHoursToSeconds(session.getWorkedHours),
        check_in: session.relCheckInLog || null,
        check_out: session.relCheckOutLog || null,
        location_status: status === "completed" ?
            getCompletedSessionLocationStatus(session) :
            "unknown",
        zone_radius: getSessionZoneRadius(session),
        failed_checkout: adaptFailedCheckoutSummary(session.getFailedCheckoutSummary),
    };
};

const sortAndAdaptSessions = (sessions) => sessions
    .map((session, index) => ({
        index,
        session: adaptSession(session),
    }))
    .sort((left, right) => {
        const leftIsValid = isValidDate(left.session.check_in_at);
        const rightIsValid = isValidDate(right.session.check_in_at);

        if (leftIsValid && rightIsValid)
        {
            const difference = moment(left.session.check_in_at).valueOf() -
                moment(right.session.check_in_at).valueOf();

            return difference || left.index - right.index;
        }

        if (leftIsValid !== rightIsValid)
        {
            return leftIsValid ? -1 : 1;
        }

        return left.index - right.index;
    })
    .map(({ session }) => session);

const getRowWarnings = (sessions, status, totalWorkedSeconds) => {
    if (status === "completed" && totalWorkedSeconds > NINE_HOURS_IN_SECONDS)
    {
        return [ STAFF_ATTENDANCE_WARNING_CODES.WORKED_MORE_THAN_NINE_HOURS ];
    }

    const hasLongOpenSession = status === "in_progress" && sessions.some((session) => (
        getSessionStatus(session) === "in_progress" &&
        isValidDate(session.check_in_at) &&
        moment().diff(moment(session.check_in_at), "seconds") > NINE_HOURS_IN_SECONDS
    ));

    return hasLongOpenSession ?
        [ STAFF_ATTENDANCE_WARNING_CODES.IN_PROGRESS_OVER_NINE_HOURS ] :
        [];
};

const sumNullableSeconds = (values) => {
    const validValues = values.filter((value) => value !== null);

    return validValues.length ? validValues.reduce((total, value) => total + value, 0) : null;
};

const aggregateWorkerRows = (group, groupBy) => {
    const buckets = new Map();

    (group?.workSessions || []).filter(Boolean).forEach((session, index) => {
        const userKey = String(getSessionUserKey(session, index));
        const workDate = getSessionWorkDate(session, groupBy, group?.date);
        const bucketKey = groupBy === "subcontractor" ?
            `${ userKey }:${ workDate || session.id || index }` :
            userKey;

        if (!buckets.has(bucketKey))
        {
            buckets.set(bucketKey, {
                key: bucketKey,
                workDate,
                sessions: [],
            });
        }

        buckets.get(bucketKey).sessions.push(session);
    });

    return [ ...buckets.values() ].map(({ key, workDate, sessions: rawSessions }) => {
        const sessions = sortAndAdaptSessions(rawSessions);
        const worker = sessions.find((session) => session.relUser)?.relUser || {};
        const sessionSeconds = sessions.map((session) => (
            staffAttendanceHoursToSeconds(session.getWorkedHours)
        ));
        const totalWorkedSeconds = sumNullableSeconds(sessionSeconds);
        const status = getStatusByPrecedence(sessions);

        return {
            id: `${ group?.id || group?.date || group?.subcontractor?.id || "group" }-${ key }`,
            worker,
            subcontractor: worker.subcontractor || group?.subcontractor || null,
            work_date: workDate,
            first_check_in: getDateBoundary(sessions, "check_in_at", "min"),
            last_check_out: getDateBoundary(sessions, "check_out_at", "max", true),
            total_worked_seconds: totalWorkedSeconds,
            status,
            warnings: getRowWarnings(sessions, status, totalWorkedSeconds),
            sessions_count: sessions.length,
            sessions,
        };
    });
};

const getFallbackGroupTotal = (workers) => sumNullableSeconds(
    workers.map((worker) => worker.total_worked_seconds)
);

const getGroupWorkersCount = (group, workers) => {
    if (group?.workersCount === null || group?.workersCount === undefined || group?.workersCount === "")
    {
        return workers.length;
    }

    const workersCount = Number(group?.workersCount);

    return Number.isFinite(workersCount) && workersCount >= 0 ? workersCount : workers.length;
};

const adaptGroup = (group, groupBy, index) => {
    const workers = aggregateWorkerRows(group, groupBy);
    const backendTotal = staffAttendanceHoursToSeconds(group?.totalHours);
    const fallbackID = groupBy === "subcontractor" ?
        `subcontractor-${ group?.subcontractor?.id || index }` :
        `date-${ group?.date || index }`;

    return {
        id: String(group?.id || fallbackID),
        group_by: groupBy,
        date: group?.date || null,
        subcontractor: group?.subcontractor || null,
        workers_count: getGroupWorkersCount(group, workers),
        total_worked_seconds: backendTotal === null ? getFallbackGroupTotal(workers) : backendTotal,
        initially_expanded: false,
        workers,
    };
};

const sortGroups = (groups, groupBy) => [ ...groups ].sort((left, right) => {
    if (groupBy === "subcontractor")
    {
        return String(left.subcontractor?.title || "")
            .localeCompare(String(right.subcontractor?.title || ""));
    }

    const leftDate = isValidDate(left.date) ? moment(left.date).valueOf() : Number.NEGATIVE_INFINITY;
    const rightDate = isValidDate(right.date) ? moment(right.date).valueOf() : Number.NEGATIVE_INFINITY;

    return rightDate - leftDate;
});

export const staffAttendanceHoursToSeconds = (value) => {
    if (value === null || value === undefined || value === "")
    {
        return null;
    }

    const hours = Number(value);

    return Number.isFinite(hours) && hours >= 0 ? Math.round(hours * 3600) : null;
};

export const formatStaffAttendanceDuration = (value) => {
    if (value === null || value === undefined || value === "")
    {
        return "—";
    }

    const seconds = Number(value);

    if (!Number.isFinite(seconds) || seconds < 0)
    {
        return "—";
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    return `${ hours } h ${ minutes } min`;
};

export const formatStaffAttendanceDate = (value) => (
    isValidDate(value) ? moment(value).format("DD.MM.YYYY") : "—"
);

export const formatStaffAttendanceTime = (value) => (
    isValidDate(value) ? moment(value).format("HH:mm") : "—"
);

export const formatStaffAttendanceDateTime = (value) => (
    isValidDate(value) ? moment(value).format("DD.MM.YYYY, HH:mm") : "—"
);

export const buildStaffAttendanceVariables = ({
    eeID,
    searchText = "",
    period = [],
    subcontractorID,
} = {}) => {
    const normalizedText = searchText.trim();
    const hasPeriod = period?.[ 0 ]?.isValid?.() && period?.[ 1 ]?.isValid?.();

    return {
        eeID: eeID === null || eeID === undefined ? undefined : String(eeID),
        text: normalizedText || undefined,
        dateFrom: hasPeriod ?
            period[ 0 ].format(API_DATE_FORMAT) :
            undefined,
        dateTo: hasPeriod ?
            period[ 1 ].format(API_DATE_FORMAT) :
            undefined,
        subcontractorID: subcontractorID === null || subcontractorID === undefined || subcontractorID === "" ?
            undefined :
            String(subcontractorID),
    };
};

export const getStaffAttendanceQuery = (groupBy = "date") => (
    groupBy === "subcontractor" ?
        GET_STAFF_MANAGEMENT_ATTENDANCE_BY_SUBCONTRACTOR :
        GET_STAFF_MANAGEMENT_ATTENDANCE_BY_DATE
);

export const getStaffAttendanceModel = (groupBy = "date") => (
    groupBy === "subcontractor" ?
        "workSessionGroupBySubcontractor" :
        "workSessionGroupByDate"
);

export const adaptStaffAttendanceGroups = (groups = [], groupBy = "date") => sortGroups(
    groups.filter(Boolean).map((group, index) => adaptGroup(group, groupBy, index)),
    groupBy
).map((group, index) => ({
    ...group,
    initially_expanded: index === 0,
}));

export const paginateStaffAttendanceGroups = (
    groups = [],
    page = 1,
    perPage = STAFF_ATTENDANCE_PAGE_SIZE
) => {
    const safePerPage = Math.max(1, Number(perPage) || STAFF_ATTENDANCE_PAGE_SIZE);
    const total = groups.length;
    const lastPage = Math.max(1, Math.ceil(total / safePerPage));
    const parsedPage = Number(page);
    const requestedPage = Number.isInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1;
    const currentPage = Math.min(requestedPage, lastPage);
    const start = (currentPage - 1) * safePerPage;
    const data = groups.slice(start, start + safePerPage).map((group, index) => ({
        ...group,
        initially_expanded: index === 0,
    }));

    return {
        data,
        paginatorInfo: {
            total,
            count: data.length,
            currentPage,
            perPage: safePerPage,
            lastPage,
        },
    };
};
